/**
 * Employee Detail Page Component
 *
 * React implementation of employee detail view with HIPAA-compliant data presentation.
 * Ported from AngularJS directory module with modern React patterns.
 *
 * Features:
 * - Full employee profile display
 * - Emergency contact information
 * - Credentials management with expiration tracking
 * - Photo display with fallback
 * - Location assignments
 * - Healthcare context integration
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Avatar,
  Descriptions,
  Tag,
  Typography,
  Space,
  Divider,
  Alert,
  Spin,
  Button,
  Badge,
  Collapse
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import { useDirectoryApi, type Employee } from './hooks/useDirectoryApi';
import './EmployeeDetailPage.css';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface EmployeeCredential {
  id: number;
  name: string;
  deleted: boolean;
  expired?: boolean;
  expires_soon?: boolean;
  latest_record?: {
    id: number;
    expires_on?: string;
    issued_on?: string;
    created_on?: string;
    scans?: Array<{
      id: number;
      url: string;
      filename?: string;
    }>;
  };
  records: Array<{
    id: number;
    expires_on?: string;
    issued_on?: string;
    created_on?: string;
    deleted: boolean;
    scans?: Array<{
      id: number;
      url: string;
      filename?: string;
    }>;
  }>;
}

interface EmployeeDetail extends Employee {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  title?: string;
  email_tec?: string;
  email_personal?: string;
  phone_mobile?: string;
  emergency_contact_name?: string;
  emergency_contact_phone_mobile?: string;
  emergency_contact_phone_home?: string;
  locations?: string[];
  credentials?: EmployeeCredential[];
  photo?: string;
}

const EmployeeDetailPage: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  const { fetchEmployeeDetails, fetchEmployeeCredentials } = useDirectoryApi();

  useEffect(() => {
    if (employeeId) {
      loadEmployeeDetails();
    }
  }, [employeeId]);

  const loadEmployeeDetails = async () => {
    if (!employeeId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch employee basic details
      const data = await fetchEmployeeDetails(employeeId);
      setEmployee(data);

      // Fetch credentials in background
      loadCredentials(employeeId);
    } catch (err) {
      console.error('Failed to load employee details:', err);
      setError('Failed to load employee details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCredentials = async (empId: string) => {
    setCredentialsLoading(true);
    try {
      const credentials = await fetchEmployeeCredentials(empId);

      // Process credentials similar to AngularJS implementation
      const validCredentials = credentials
        .filter(c => !c.deleted)
        .map(cred => {
          // Find latest record (assuming records are sorted in reverse order by ID)
          const latestRecord = cred.records.find(r => !r.deleted) || cred.records[0];

          // Check expiration status
          const expired = latestRecord?.expires_on
            ? new Date(latestRecord.expires_on) < new Date()
            : false;

          const expiresSoon = latestRecord?.expires_on
            ? new Date(latestRecord.expires_on) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            : false;

          return {
            ...cred,
            latest_record: latestRecord,
            expired,
            expires_soon: expiresSoon && !expired
          };
        });

      setEmployee(prev => prev ? { ...prev, credentials: validCredentials } : null);
    } catch (err) {
      console.error('Failed to load employee credentials:', err);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const getDisplayEmail = (emp: EmployeeDetail): string => {
    return emp.email_tec || emp.email_personal || '';
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCredentialStatus = (credential: EmployeeCredential) => {
    if (credential.expired) {
      return { color: 'error', icon: <ExclamationCircleOutlined />, text: 'Expired' };
    }
    if (credential.expires_soon) {
      return { color: 'warning', icon: <ClockCircleOutlined />, text: 'Expires Soon' };
    }
    return { color: 'success', icon: <CheckCircleOutlined />, text: 'Valid' };
  };

  const getInitials = (emp: EmployeeDetail): string => {
    const first = emp.first_name || emp.firstName || '';
    const last = emp.last_name || emp.lastName || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const getFullName = (emp: EmployeeDetail): string => {
    const first = emp.first_name || emp.firstName || '';
    const middle = emp.middle_name || '';
    const last = emp.last_name || emp.lastName || '';
    return `${first}${middle ? ' ' + middle : ''} ${last}`.trim();
  };

  if (loading) {
    return (
      <div className="employee-detail-loading">
        <Spin size="large" tip="Loading employee details..." />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="employee-detail-error">
        <Alert
          message="Error Loading Employee"
          description={error || 'Employee not found'}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate('/directory')}>
              Back to Directory
            </Button>
          }
        />
      </div>
    );
  }

  const displayEmail = getDisplayEmail(employee);
  const fullName = getFullName(employee);
  const validCredentials = employee.credentials?.filter(c => !c.deleted) || [];

  return (
    <div className="employee-detail-page">
      <div className="employee-detail-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/directory')}
          className="back-button"
        >
          Back to Directory
        </Button>
      </div>

      <Card className="employee-detail-card">
        {/* Header Section */}
        <div className="employee-header">
          <Space size="large" align="start">
            {employee.photo ? (
              <Avatar
                size={120}
                src={employee.photo}
                alt={fullName}
                className="employee-avatar"
              />
            ) : (
              <Avatar
                size={120}
                style={{ backgroundColor: '#1890ff', fontSize: '48px' }}
                className="employee-avatar"
              >
                {getInitials(employee)}
              </Avatar>
            )}

            <div className="employee-info">
              <Title level={2} style={{ marginBottom: '8px' }}>
                {fullName}
              </Title>
              {employee.title && (
                <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '12px' }}>
                  {employee.title}
                </Text>
              )}
              {employee.role && (
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {employee.role}
                </Tag>
              )}
            </div>
          </Space>
        </div>

        <Divider />

        {/* Contact Information */}
        {(displayEmail || employee.phone_mobile) && (
          <>
            <Title level={3}>
              <MailOutlined /> Contact Information
            </Title>
            <Descriptions column={1} bordered size="middle">
              {displayEmail && (
                <Descriptions.Item label="Email">
                  <a href={`mailto:${displayEmail}`}>{displayEmail}</a>
                </Descriptions.Item>
              )}
              {employee.phone_mobile && (
                <Descriptions.Item label="Phone">
                  <a href={`tel:${employee.phone_mobile}`}>{employee.phone_mobile}</a>
                </Descriptions.Item>
              )}
            </Descriptions>
            <Divider />
          </>
        )}

        {/* Emergency Contact */}
        <Title level={3}>
          <PhoneOutlined /> Emergency Contact
        </Title>
        {employee.emergency_contact_name ||
         employee.emergency_contact_phone_mobile ||
         employee.emergency_contact_phone_home ? (
          <Descriptions column={1} bordered size="middle">
            {employee.emergency_contact_name && (
              <Descriptions.Item label="Name">
                {employee.emergency_contact_name}
              </Descriptions.Item>
            )}
            {employee.emergency_contact_phone_mobile && (
              <Descriptions.Item label="Mobile">
                <a href={`tel:${employee.emergency_contact_phone_mobile}`}>
                  {employee.emergency_contact_phone_mobile}
                </a>
              </Descriptions.Item>
            )}
            {employee.emergency_contact_phone_home && (
              <Descriptions.Item label="Home">
                <a href={`tel:${employee.emergency_contact_phone_home}`}>
                  {employee.emergency_contact_phone_home}
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Alert
            message="No Emergency Contact specified!"
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
          />
        )}

        {/* Locations */}
        {employee.locations && employee.locations.length > 0 && (
          <>
            <Divider />
            <Title level={3}>
              <EnvironmentOutlined /> Locations
            </Title>
            <Space direction="vertical" size="small">
              {employee.locations.map((location, index) => (
                <Tag key={index} icon={<EnvironmentOutlined />} color="geekblue">
                  {location}
                </Tag>
              ))}
            </Space>
          </>
        )}

        {/* Credentials */}
        {validCredentials.length > 0 && (
          <>
            <Divider />
            <Title level={3}>
              <SafetyCertificateOutlined /> Credentials
              {credentialsLoading && <Spin size="small" style={{ marginLeft: '12px' }} />}
            </Title>
            <Collapse accordion className="credentials-collapse">
              {validCredentials.map((credential) => {
                const status = getCredentialStatus(credential);
                return (
                  <Panel
                    header={
                      <Space>
                        <Badge status={status.color as any} />
                        <strong>{credential.name}</strong>
                        <Tag color={status.color} icon={status.icon}>
                          {status.text}
                        </Tag>
                      </Space>
                    }
                    key={credential.id}
                    className={`credential-panel ${credential.expired ? 'expired' : credential.expires_soon ? 'expires-soon' : 'normal'}`}
                  >
                    {credential.latest_record ? (
                      <div className="credential-details">
                        {credential.latest_record.expires_on && (
                          <Paragraph>
                            <strong>Expires on:</strong> {formatDate(credential.latest_record.expires_on)}
                          </Paragraph>
                        )}
                        {credential.latest_record.issued_on && (
                          <Paragraph>
                            <strong>Issued on:</strong> {formatDate(credential.latest_record.issued_on)}
                          </Paragraph>
                        )}
                        {credential.latest_record.scans && credential.latest_record.scans.length > 0 && (
                          <div className="credential-scans">
                            <Paragraph strong>Attached Documents:</Paragraph>
                            <Space wrap>
                              {credential.latest_record.scans.map((scan) => (
                                <Button
                                  key={scan.id}
                                  icon={<FileImageOutlined />}
                                  href={scan.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Document
                                </Button>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Alert
                        message="No Valid Credentials"
                        type="info"
                        showIcon
                      />
                    )}
                  </Panel>
                );
              })}
            </Collapse>
          </>
        )}
      </Card>
    </div>
  );
};

export default EmployeeDetailPage;
